// =============================================================================
// app/src/lib/folhaSaida.ts — as folhas da lista de contas, em PDF e em foto.
//
// As folhas vêm prontas de listaDeContas.ts (peças posicionadas em mm, Y para
// baixo). Aqui elas só são REDESENHADAS, peça por peça, de dois jeitos:
//
//   * PDF, pelo pdf.ts — um arquivo só, várias páginas, poucos KB;
//   * PNG, num canvas — uma foto por folha, que o WhatsApp mostra direto na
//     conversa, sem abrir nada.
//
// Os dois percorrem a MESMA lista de peças, na mesma ordem: se um mostra uma
// conta, o outro mostra a mesma, no mesmo lugar. A métrica do texto é a da
// Helvetica nos dois (a Arial, que o canvas usa onde não há Helvetica, foi
// desenhada com as mesmas larguras) — a quebra de linha calculada vale igual.
// =============================================================================
import { CAMINHOS_MARCA, VIEWBOX_MARCA } from './marca';
import { novoPdf } from './pdf';
import { interpretarCaminho, type Segmento } from './svgPath';
import type { Folha, Peca } from './listaDeContas';

const VIEWBOX = VIEWBOX_MARCA as unknown as readonly [number, number, number, number];

/** Os caminhos do S, interpretados uma vez só. */
let marcaInterpretada: Segmento[][] | null = null;
const segmentosDaMarca = () => (marcaInterpretada ??= CAMINHOS_MARCA.map(interpretarCaminho));

/** As folhas num PDF só, uma página por folha. */
export function folhasEmPdf(folhas: ReadonlyArray<Folha>): Uint8Array {
  const primeira = folhas[0];
  const pdf = novoPdf(primeira ? { larguraMm: primeira.larguraMm, alturaMm: primeira.alturaMm } : {});
  folhas.forEach((folha, i) => {
    if (i > 0) pdf.novaPagina();
    for (const p of folha.pecas) desenharNoPdf(pdf, p);
  });
  return pdf.bytes();
}

function desenharNoPdf(pdf: ReturnType<typeof novoPdf>, p: Peca): void {
  switch (p.tipo) {
    case 'texto':
      pdf.texto(p.texto, p.x, p.y, { tamanho: p.tamanho, negrito: p.negrito, alinhamento: p.alinhamento, cinza: p.cinza });
      return;
    case 'linha':
      pdf.linha(p.x1, p.y1, p.x2, p.y2, { espessura: p.espessura, cinza: p.cinza });
      return;
    case 'retangulo':
      pdf.retangulo(p.x, p.y, p.largura, p.altura, p.cinza);
      return;
    case 'marca':
      for (const seg of segmentosDaMarca()) pdf.caminho(seg, VIEWBOX, { xMm: p.x, yMm: p.y, larguraMm: p.largura }, p.cinza);
  }
}

// --- A foto ------------------------------------------------------------------------

/** Pixels por milímetro: a folha de 102,4 mm vira 1024 px. */
export const PX_POR_MM = 10;

/** O pedaço do contexto 2D que o desenho usa — declarado aqui para os testes
 *  passarem um falso (o vitest roda em node, sem canvas). */
export interface Contexto2D {
  fillStyle: string | CanvasGradient | CanvasPattern;
  strokeStyle: string | CanvasGradient | CanvasPattern;
  lineWidth: number;
  font: string;
  textAlign: CanvasTextAlign;
  textBaseline: CanvasTextBaseline;
  setTransform(a: number, b: number, c: number, d: number, e: number, f: number): void;
  fillRect(x: number, y: number, w: number, h: number): void;
  fillText(texto: string, x: number, y: number): void;
  beginPath(): void;
  moveTo(x: number, y: number): void;
  lineTo(x: number, y: number): void;
  bezierCurveTo(a: number, b: number, c: number, d: number, e: number, f: number): void;
  closePath(): void;
  stroke(): void;
  fill(regra?: CanvasFillRule): void;
}

export interface Tela {
  width: number;
  height: number;
  getContext(tipo: '2d'): Contexto2D | null;
  toDataURL(tipo: string): string;
}

/** Cinza do pdf.ts (0 = preto, 1 = branco) em cor do canvas. */
const cor = (cinza: number) => {
  const v = Math.round(Math.min(1, Math.max(0, cinza)) * 255);
  return `rgb(${v}, ${v}, ${v})`;
};

/** 1 pt em mm — o corpo do texto vem em pontos, como no PDF. */
const PT = 25.4 / 72;

/**
 * Uma folha numa foto PNG, como `data:` URL.
 *
 * `data:` e não blob: — a CSP do sistema (Caddyfile, img-src) aceita data: e
 * recusa blob:, e a mesma string serve de prévia na tela e vira o arquivo
 * enviado (fotoParaArquivo).
 */
export function folhaEmPng(folha: Folha, criarTela: () => Tela): string {
  const tela = criarTela();
  tela.width = Math.round(folha.larguraMm * PX_POR_MM);
  tela.height = Math.round(folha.alturaMm * PX_POR_MM);
  const ctx = tela.getContext('2d');
  if (!ctx) throw new Error('Este aparelho não conseguiu desenhar a foto. Envie em PDF.');
  try {
    // Fundo BRANCO primeiro: PNG transparente no modo escuro do WhatsApp
    // mostraria letra preta sobre fundo preto.
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, tela.width, tela.height);
    // Daqui em diante, a unidade é o milímetro, como nas peças.
    ctx.setTransform(PX_POR_MM, 0, 0, PX_POR_MM, 0, 0);
    ctx.textBaseline = 'alphabetic'; // o mesmo ponto de apoio do Tm do PDF
    for (const p of folha.pecas) desenharNoCanvas(ctx, p);
    return tela.toDataURL('image/png');
  } finally {
    // Solta a memória já: o iPhone tem teto de canvas somado, e são até 8.
    tela.width = 0;
    tela.height = 0;
  }
}

function desenharNoCanvas(ctx: Contexto2D, p: Peca): void {
  switch (p.tipo) {
    case 'texto':
      ctx.fillStyle = cor(p.cinza);
      ctx.font = `${p.negrito ? 'bold ' : ''}${(p.tamanho * PT).toFixed(3)}px Helvetica, Arial, sans-serif`;
      ctx.textAlign = p.alinhamento === 'centro' ? 'center' : p.alinhamento === 'direita' ? 'right' : 'left';
      ctx.fillText(p.texto, p.x, p.y);
      return;
    case 'linha':
      ctx.strokeStyle = cor(p.cinza);
      ctx.lineWidth = p.espessura;
      ctx.beginPath();
      ctx.moveTo(p.x1, p.y1);
      ctx.lineTo(p.x2, p.y2);
      ctx.stroke();
      return;
    case 'retangulo':
      ctx.fillStyle = cor(p.cinza);
      ctx.fillRect(p.x, p.y, p.largura, p.altura);
      return;
    case 'marca': {
      const [minX, minY, vbLargura] = VIEWBOX;
      const escala = p.largura / vbLargura;
      const X = (x: number) => p.x + (x - minX) * escala;
      const Y = (y: number) => p.y + (y - minY) * escala;
      ctx.fillStyle = cor(p.cinza);
      for (const segmentos of segmentosDaMarca()) {
        ctx.beginPath();
        for (const s of segmentos) {
          if (s.tipo === 'm') ctx.moveTo(X(s.x), Y(s.y));
          else if (s.tipo === 'l') ctx.lineTo(X(s.x), Y(s.y));
          else if (s.tipo === 'c') ctx.bezierCurveTo(X(s.x1), Y(s.y1), X(s.x2), Y(s.y2), X(s.x), Y(s.y));
          else ctx.closePath();
        }
        // Par-ímpar, como o f* do PDF: o S tem contorno interno.
        ctx.fill('evenodd');
      }
    }
  }
}

/** A foto (data: URL) vira o arquivo que o Compartilhar leva. */
export function fotoParaArquivo(dataUrl: string, nome: string): File {
  const virgula = dataUrl.indexOf(',');
  const binario = atob(dataUrl.slice(virgula + 1));
  const bytes = new Uint8Array(binario.length);
  for (let i = 0; i < binario.length; i += 1) bytes[i] = binario.charCodeAt(i);
  return new File([bytes], nome, { type: 'image/png' });
}

/** O PDF vira o arquivo que o Compartilhar leva. */
export function pdfParaArquivo(bytes: Uint8Array, nome: string): File {
  return new File([bytes as BlobPart], nome, { type: 'application/pdf' });
}
